import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Trophy, Mail, Lock, User, GraduationCap as SchoolIcon, Flag } from "lucide-react";
import SiteChrome from "@/components/SiteChrome";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { NATIONALITIES } from "@/lib/nationalities";

/**
 * Public landing for the "Eliminatórias" feature.
 * - Tabs: Entrar | Criar conta | Recuperar password.
 * - On success → /eliminatorias/lobby.
 */
export default function EliminatoriasLanding() {
  const nav = useNavigate();
  const { login, auth } = useAuth();
  const { t } = useI18n();

  // If the user already has a valid elim_user session, send them to the lobby.
  React.useEffect(() => {
    if (auth?.role === "elim_user") nav("/eliminatorias/lobby", { replace: true });
  }, [auth, nav]);

  const [tab, setTab] = useState("login");

  return (
    <SiteChrome>
      <section className="max-w-5xl mx-auto px-6 pt-12 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-600/10 text-violet-700 text-xs font-bold tracking-[0.2em] uppercase mb-4">
            <Crown className="w-4 h-4" /> {t("elim_landing_eyebrow")}
          </div>
          <h1 className="text-4xl md:text-6xl font-[Fredoka] font-semibold text-brand-text leading-tight">
            {t("elim_landing_title_prefix")} <span className="text-violet-600">{t("elim_landing_title_highlight")}</span>{t("elim_landing_title_suffix")}
          </h1>
          <p className="mt-4 text-base md:text-lg text-brand-muted max-w-2xl mx-auto">
            {t("elim_landing_subtitle")}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 mt-12 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="rounded-3xl bg-brand-bg2 shadow-lg p-8 border-2 border-violet-100"
            data-testid="elim-welcome-card"
          >
            <h2 className="font-[Fredoka] text-2xl font-bold text-brand-text mb-3 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-amber-500" /> {t("elim_landing_how_it_works")}
            </h2>
            <ul className="space-y-3 text-brand-text text-sm leading-relaxed">
              <li className="flex gap-2"><span className="text-violet-600 font-bold">1.</span> {t("elim_landing_step_1")}</li>
              <li className="flex gap-2"><span className="text-violet-600 font-bold">2.</span> {t("elim_landing_step_2")}</li>
              <li className="flex gap-2"><span className="text-violet-600 font-bold">3.</span> {t("elim_landing_step_3")}</li>
              <li className="flex gap-2"><span className="text-violet-600 font-bold">4.</span> {t("elim_landing_step_4")}</li>
            </ul>
            <div className="mt-6 rounded-2xl bg-violet-50 p-4 text-xs text-violet-800 border border-violet-200">
              <strong>{t("elim_landing_important_label")}</strong> {t("elim_landing_important_body")}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
            className="rounded-3xl bg-brand-bg2 shadow-xl p-6 md:p-8 border-2 border-brand-bg2"
          >
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="grid grid-cols-3 w-full bg-brand-bg2 rounded-full p-1 mb-6">
                <TabsTrigger value="login" data-testid="elim-tab-login" className="rounded-full data-[state=active]:bg-brand-bg2 data-[state=active]:shadow">{t("elim_landing_tab_login")}</TabsTrigger>
                <TabsTrigger value="register" data-testid="elim-tab-register" className="rounded-full data-[state=active]:bg-brand-bg2 data-[state=active]:shadow">{t("elim_landing_tab_register")}</TabsTrigger>
                <TabsTrigger value="forgot" data-testid="elim-tab-forgot" className="rounded-full data-[state=active]:bg-brand-bg2 data-[state=active]:shadow">{t("elim_landing_tab_forgot")}</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <LoginForm onDone={(d) => { login(d); nav("/eliminatorias/lobby"); }} />
              </TabsContent>
              <TabsContent value="register">
                <RegisterForm onDone={(d) => { login(d); nav("/eliminatorias/lobby"); }} switchToLogin={() => setTab("login")} />
              </TabsContent>
              <TabsContent value="forgot">
                <ForgotForm onSwitchToLogin={() => setTab("login")} />
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>

        <div className="mt-10 text-center">
          <Link to="/" className="text-sm text-brand-muted hover:text-violet-700 underline" data-testid="elim-back-home">{t("elim_landing_back_home")}</Link>
        </div>
      </section>
    </SiteChrome>
  );
}

function LoginForm({ onDone }) {
  const { t } = useI18n();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      toast.error(t("err_fill_all"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/eliminatoria/login", { identifier: identifier.trim(), password });
      toast.success(t("ok_welcome_named", { name: data.name || data.extra?.username || "" }));
      onDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_login_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" autoComplete="off">
      <div>
        <Label className="flex items-center gap-2 mb-1.5"><User className="w-4 h-4 text-brand-muted" /> {t("elim_login_username_label")}</Label>
        <Input data-testid="elim-login-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder={t("elim_login_username_ph")} autoComplete="off" />
      </div>
      <div>
        <Label className="flex items-center gap-2 mb-1.5"><Lock className="w-4 h-4 text-brand-muted" /> {t("password")}</Label>
        <Input data-testid="elim-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("elim_login_password_ph")} autoComplete="new-password" />
      </div>
      <Button data-testid="elim-login-submit" type="submit" disabled={loading} className="w-full bg-violet-600 hover:bg-violet-700 rounded-full font-bold h-11">
        {loading ? t("elim_login_submitting") : t("elim_login_submit")}
      </Button>
    </form>
  );
}

function RegisterForm({ onDone, switchToLogin }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    username: "", email: "", password: "", confirm: "",
    name: "", school: "", nationality: "PT",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.email || !form.password) {
      toast.error(t("err_required_fields"));
      return;
    }
    if (form.password.length < 6) {
      toast.error(t("err_password_too_short"));
      return;
    }
    if (form.password !== form.confirm) {
      toast.error(t("err_passwords_no_match"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/eliminatoria/register", {
        username: form.username.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim() || null,
        school: form.school.trim() || null,
        nationality: form.nationality || null,
      });
      toast.success(t("ok_account_created"));
      onDone(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("err_register_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" autoComplete="off">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="flex items-center gap-2 mb-1.5"><User className="w-4 h-4 text-brand-muted" /> {t("elim_reg_username_label")}</Label>
          <Input data-testid="elim-reg-username" value={form.username} onChange={onChange("username")} placeholder={t("elim_reg_username_ph")} autoComplete="off" />
        </div>
        <div>
          <Label className="flex items-center gap-2 mb-1.5"><Mail className="w-4 h-4 text-brand-muted" /> {t("elim_reg_email_label")}</Label>
          <Input data-testid="elim-reg-email" type="email" value={form.email} onChange={onChange("email")} placeholder={t("elim_reg_email_ph")} autoComplete="off" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="flex items-center gap-2 mb-1.5"><Lock className="w-4 h-4 text-brand-muted" /> {t("elim_reg_password_label")}</Label>
          <Input data-testid="elim-reg-password" type="password" value={form.password} onChange={onChange("password")} placeholder={t("elim_reg_password_ph")} autoComplete="new-password" />
        </div>
        <div>
          <Label className="flex items-center gap-2 mb-1.5"><Lock className="w-4 h-4 text-brand-muted" /> {t("elim_reg_confirm_label")}</Label>
          <Input data-testid="elim-reg-confirm" type="password" value={form.confirm} onChange={onChange("confirm")} placeholder={t("elim_reg_confirm_ph")} autoComplete="new-password" />
        </div>
      </div>
      <div className="border-t border-brand-bg2 pt-4 mt-2">
        <p className="text-xs uppercase tracking-wider text-brand-muted font-bold mb-3">{t("elim_reg_profile_hint")}</p>
        <div className="space-y-3">
          <div>
            <Label className="flex items-center gap-2 mb-1.5"><User className="w-4 h-4 text-brand-muted" /> {t("elim_reg_name_label")}</Label>
            <Input data-testid="elim-reg-name" value={form.name} onChange={onChange("name")} placeholder={t("elim_reg_name_ph")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-2 mb-1.5"><SchoolIcon className="w-4 h-4 text-brand-muted" /> {t("elim_reg_school_label")}</Label>
              <Input data-testid="elim-reg-school" value={form.school} onChange={onChange("school")} placeholder={t("elim_reg_school_ph")} />
            </div>
            <div>
              <Label className="flex items-center gap-2 mb-1.5"><Flag className="w-4 h-4 text-brand-muted" /> {t("elim_reg_nationality_label")}</Label>
              <Select value={form.nationality} onValueChange={(v) => setForm((f) => ({ ...f, nationality: v }))}>
                <SelectTrigger data-testid="elim-reg-nationality"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NATIONALITIES.map((n) => (
                    <SelectItem key={n.code} value={n.code}>{n.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
      <Button data-testid="elim-reg-submit" type="submit" disabled={loading} className="w-full bg-violet-600 hover:bg-violet-700 rounded-full font-bold h-11">
        {loading ? t("elim_reg_submitting") : t("elim_reg_submit")}
      </Button>
      <div className="text-center text-xs text-brand-muted">
        {t("elim_reg_has_account")}{" "}
        <button type="button" onClick={switchToLogin} className="text-violet-600 font-bold hover:underline" data-testid="elim-reg-go-login">{t("elim_reg_go_login")}</button>
      </div>
    </form>
  );
}

function ForgotForm({ onSwitchToLogin }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(null); // null | {dev_token, dev_reset_url}
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/eliminatoria/forgot-password", { email: email.trim().toLowerCase() });
      setSent({ dev_token: data.dev_token, dev_reset_url: data.dev_reset_url });
      toast.success(data.message || t("elim_forgot_request_body"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_forgot_failed"));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-4" data-testid="elim-forgot-sent">
        <div className="rounded-2xl bg-green-50 border-2 border-green-200 p-4 text-sm text-green-800">
          <strong>{t("elim_forgot_request_sent")}</strong> {t("elim_forgot_request_body")}
        </div>
        {sent.dev_token && (
          <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-4 text-xs text-amber-900">
            <strong>{t("elim_forgot_test_mode_label")}</strong> {t("elim_forgot_test_mode_hint")}
            <div className="mt-2">
              <Link
                to={sent.dev_reset_url || `/eliminatorias/reset-password?token=${sent.dev_token}`}
                className="inline-block px-4 py-2 rounded-full bg-amber-500 text-white font-bold hover:bg-amber-600"
                data-testid="elim-forgot-dev-link"
              >
                {t("elim_forgot_open_reset")}
              </Link>
            </div>
          </div>
        )}
        <Button type="button" onClick={onSwitchToLogin} variant="ghost" className="w-full">{t("elim_forgot_back")}</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-brand-muted">{t("elim_forgot_hint")}</p>
      <div>
        <Label className="flex items-center gap-2 mb-1.5"><Mail className="w-4 h-4 text-brand-muted" /> {t("elim_forgot_email_label")}</Label>
        <Input data-testid="elim-forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("elim_reg_email_ph")} />
      </div>
      <Button data-testid="elim-forgot-submit" type="submit" disabled={loading} className="w-full bg-violet-600 hover:bg-violet-700 rounded-full font-bold h-11">
        {loading ? t("elim_forgot_submitting") : t("elim_forgot_submit")}
      </Button>
      <div className="text-center text-xs text-brand-muted">
        <button type="button" onClick={onSwitchToLogin} className="text-violet-600 font-bold hover:underline">{t("elim_forgot_back")}</button>
      </div>
    </form>
  );
}
